/**
 * /admin/omie/sync-status — situação da sincronização com o Omie
 * (R-IMPORT 3 A1.9 + B-FINAL.6.1).
 *
 * - Contador de 14 dias só em leitura
 * - Situação do envio de dados (desligado / simulação / ligado)
 * - Última sincronização por empresa + tipo
 * - Botão "Sincronizar agora" por tipo
 * - BUG-007 fix: opção "cadastrar clientes que só existem no Omie"
 * - BUG-008 fix: botão "testar acesso" por empresa
 *
 * FASE-C (redesign): a matriz empresa × 5 tipos era um `<table>` cru de 7
 * colunas (data + botão em cada célula) dentro de um card sem rolagem —
 * estourava +532px no celular de 375px, o pior overflow do sistema. Como o
 * sistema tem poucas empresas e SEMPRE os mesmos 5 tipos, a matriz virou um
 * CARD POR EMPRESA com os 5 tipos numa grade que responde à largura
 * (1 coluna no celular → 2 → 3). Mesma informação, mesmas ações, mesmos
 * `data-testid`, zero rolagem lateral.
 */
import { useEffect, useState } from 'react';

import { Alert, Aviso, Badge, Button, Card, Checkbox, HelpTip, KpiCard, PageHeader } from '@/components/ui';
import { api } from '@/lib/api';

import { formatDataHoraSegundos } from '@/lib/format-date';
interface SyncStatus {
  writeMode: 'disabled' | 'dry_run' | 'live';
  diasReadOnly: number;
  peloMenos1SyncCadaTipo: boolean;
  empresasComCredenciais: string[];
  ultimosSync: Array<{
    empresa_id: string;
    tipo: string;
    ultimo_iniciado: string;
    ultimo_finalizado: string;
  }>;
  /** SYNC-ROBUSTO (21/08): a última execução de cada (empresa, tipo), com a história do motor. */
  execucoes?: Execucao[];
  /** 🔴 19/10/2026: o atraso de cada (empresa, tipo) — a última leitura CONCLUÍDA contra 4 ciclos da cadência. */
  atrasos?: Atraso[];
}

interface Atraso {
  empresaId: string;
  tipo: string;
  atrasado: boolean;
  ultimaConcluidaIso: string | null;
  minutosSemLeitura: number | null;
  limiteMinutos: number;
  cadenciaMinutos: number;
  ultimoErro: string | null;
}

/** "há 3 dias", "há 5 horas", "há 40 minutos" — a mesma escrita do aviso do sininho (`sync-atraso.ts`). */
function tempoSemLeitura(minutos: number | null): string {
  if (minutos === null) return 'nunca';
  if (minutos >= 2 * 1440) return `há ${Math.floor(minutos / 1440)} dias`;
  if (minutos >= 1440) return 'há 1 dia';
  if (minutos >= 120) return `há ${Math.floor(minutos / 60)} horas`;
  if (minutos >= 60) return 'há 1 hora';
  return `há ${minutos} minutos`;
}

interface Execucao {
  empresaId: string;
  tipo: string;
  status: string;
  quandoIso: string;
  modo: 'completo' | 'incremental' | null;
  lidos: number;
  novos: number;
  atualizados: number;
  apagados: number | null;
  tentativas: number | null;
  duracaoMs: number | null;
  verificacao: { ok: boolean; omie: number | null; painel: number | null; detalhe: string } | null;
  erro: string | null;
  pausadoAte: string | null;
  falhasSeguidas: number;
  recuperadoAposFalhas: number | null;
}

interface CredsTestResult {
  empresaId: string;
  ok: boolean;
  status: 'env_missing' | 'invalid' | 'network' | 'unexpected' | 'ok';
  message: string;
  omieFaultstring?: string;
  totalRegistrosOmie?: number;
}

// SYNC-ROBUSTO (21/08): +contratos (Receita Recorrente/Fixa), vendedores
// (card de vendas do vendedor) e contas correntes — os syncs do motor único.
const TIPOS = [
  'clientes',
  'produtos',
  'pedidos',
  'contas_receber',
  'contas_pagar',
  'contratos',
  'vendedores',
  'contas_correntes',
] as const;

/** Nome de tela de cada tipo — o valor cru vai só pra API. */
const TIPO_LABEL: Record<(typeof TIPOS)[number], string> = {
  clientes: 'Clientes',
  produtos: 'Produtos',
  pedidos: 'Pedidos',
  contas_receber: 'Contas a receber',
  contas_pagar: 'Contas a pagar',
  contratos: 'Contratos',
  vendedores: 'Vendedores',
  contas_correntes: 'Contas correntes',
};

const MODO_LABEL: Record<string, string> = { completo: 'completa', incremental: 'só o que mudou' };

function duracao(ms: number | null): string {
  if (ms === null) return '';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

/** SYNC-ROBUSTO: a história da última execução, de relance. */
function ExecucaoResumo({ e }: { e: Execucao }) {
  const divergente = e.status === 'sucesso' && e.verificacao && !e.verificacao.ok;
  const badge =
    e.pausadoAte ? { v: 'danger' as const, t: 'Pausada' }
    : e.status === 'falha' ? { v: 'danger' as const, t: 'Falhou' }
    : divergente ? { v: 'warning' as const, t: 'Divergente' }
    : e.status === 'parcial' ? { v: 'warning' as const, t: 'Parcial' }
    : { v: 'success' as const, t: 'OK' };
  return (
    <div className="space-y-1 text-xs text-ink-2 dark:text-ink-dark-2" data-testid={`exec-${e.empresaId}-${e.tipo}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant={badge.v} size="sm">{badge.t}</Badge>
        {e.modo ? <span className="text-ink-3 dark:text-ink-dark-3">{MODO_LABEL[e.modo] ?? e.modo}</span> : null}
        {e.tentativas !== null && e.tentativas > 1 ? (
          <span className="text-ink-3 dark:text-ink-dark-3" title="Quantas chamadas ao Omie foram precisas — mais que 1 significa que o Omie limitou e o sistema esperou">
            · {e.tentativas} tentativas
          </span>
        ) : null}
        {e.duracaoMs !== null ? <span className="text-ink-3 dark:text-ink-dark-3">· {duracao(e.duracaoMs)}</span> : null}
      </div>
      {e.status !== 'falha' ? (
        <div className="break-words">
          {e.lidos} lidos · {e.novos} novos · {e.atualizados} atualizados
          {e.apagados !== null && e.apagados > 0 ? <> · <strong>{e.apagados} apagados</strong> (não existem mais no Omie)</> : null}
        </div>
      ) : null}
      {e.verificacao ? (
        <div className={divergente ? 'font-medium text-danger' : 'text-ink-3 dark:text-ink-dark-3'} data-testid={`verif-${e.empresaId}-${e.tipo}`}>
          Conferência: {e.verificacao.ok ? '✓ ' : '✗ '}
          {e.verificacao.painel !== null && e.verificacao.omie !== null
            ? `painel ${e.verificacao.painel} · Omie ${e.verificacao.omie}`
            : e.verificacao.detalhe}
        </div>
      ) : null}
      {e.erro && e.status === 'falha' ? <div className="break-words text-danger">{e.erro}</div> : null}
      {e.pausadoAte ? (
        <div className="text-danger">
          Pausada até {formatQuando(e.pausadoAte)} ({e.falhasSeguidas} falhas seguidas) — volta sozinha.
        </div>
      ) : null}
      {e.recuperadoAposFalhas ? <div className="text-success">Voltou a funcionar após {e.recuperadoAposFalhas} falha(s).</div> : null}
    </div>
  );
}

/** Modo de escrita: nome interno → frase que o admin entende. */
const ENVIO: Record<
  SyncStatus['writeMode'],
  { valor: string; contexto: string }
> = {
  disabled: { valor: 'Desligado', contexto: 'O IGM só lê dados do Omie — nada é gravado lá.' },
  dry_run: {
    valor: 'Em simulação',
    contexto: 'Os envios são registrados pra conferência, mas nada chega no Omie.',
  },
  live: { valor: 'Ligado', contexto: 'Os dados são gravados no Omie de verdade.' },
};

/** Resultado do teste de acesso: código interno → frase de gente. */
const CREDS_STATUS: Record<CredsTestResult['status'], string> = {
  ok: 'Acesso funcionando',
  env_missing: 'Falta cadastrar o acesso no servidor',
  invalid: 'O Omie recusou o acesso',
  network: 'Não foi possível falar com o Omie',
  unexpected: 'Erro inesperado ao testar',
};

function formatQuando(iso: string | undefined | null): string {
  if (!iso) return 'nunca';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'nunca';
  return formatDataHoraSegundos(d);
}

export function OmieSyncStatusPage() {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [syncingKey, setSyncingKey] = useState<string | null>(null);
  const [criarFaltantes, setCriarFaltantes] = useState(false);
  const [testingEmpresa, setTestingEmpresa] = useState<string | null>(null);
  const [credsResult, setCredsResult] = useState<Record<string, CredsTestResult>>({});
  const [lastSyncResult, setLastSyncResult] = useState<string | null>(null);

  function load() {
    setErr(null);
    api
      .get<SyncStatus>('/api/v1/admin/omie/sync/status')
      .then(setStatus)
      .catch((e: Error) => setErr(e.message));
  }
  useEffect(load, []);

  async function forcarSync(tipo: string, empresaId: string) {
    const key = `${empresaId}:${tipo}`;
    setSyncingKey(key);
    setLastSyncResult(null);
    try {
      const body: { empresaId: string; criarFaltantes?: boolean } = { empresaId };
      if (tipo === 'clientes' && criarFaltantes) body.criarFaltantes = true;
      const resp = await api.post<{
        tipo: string;
        result: {
          registrosLidos: number;
          registrosAtualizados: number;
          registrosNovos?: number;
          status: string;
        };
      }>(`/api/v1/admin/omie/sync/${tipo}`, body);
      const r = resp.result;
      const nome = TIPO_LABEL[tipo as (typeof TIPOS)[number]] ?? tipo;
      setLastSyncResult(
        `${nome} da empresa ${empresaId}: ${r.status}. ` +
          `${r.registrosLidos} lidos · ${r.registrosAtualizados} atualizados` +
          (typeof r.registrosNovos === 'number' ? ` · ${r.registrosNovos} novos` : ''),
      );
      load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSyncingKey(null);
    }
  }

  async function testarCredenciais(empresaId: string) {
    setTestingEmpresa(empresaId);
    try {
      const result = await api.post<CredsTestResult>(
        '/api/v1/admin/omie/test-credentials',
        { empresaId },
      );
      setCredsResult((prev) => ({ ...prev, [empresaId]: result }));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setTestingEmpresa(null);
    }
  }

  const cabecalho = (
    <PageHeader
      className="mb-0"
      crumb="Configurações · Integrações"
      title="Sincronização com o Omie"
      subtitle="Quando o IGM buscou cada tipo de informação no Omie, e o botão pra buscar de novo agora."
    />
  );

  if (err && !status)
    return (
      <div className="min-w-0 space-y-5" data-testid="omie-sync-status-page">
        {cabecalho}
        <Alert variant="danger" title="Não foi possível carregar a sincronização">
          Tente recarregar a página. Se continuar, avise quem cuida do servidor.
          <span className="mt-1 block break-words text-xs opacity-80">Detalhe: {err}</span>
        </Alert>
      </div>
    );

  if (!status)
    return (
      <div className="min-w-0 space-y-5" data-testid="omie-sync-status-page">
        {cabecalho}
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      </div>
    );

  const envio = ENVIO[status.writeMode];

  return (
    <div className="min-w-0 space-y-5" data-testid="omie-sync-status-page">
      {cabecalho}

      {err && (
        <Alert variant="danger" title="A última ação não deu certo">
          Confira a mensagem abaixo e tente novamente.
          <span className="mt-1 block break-words text-xs opacity-80">Detalhe: {err}</span>
        </Alert>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard
          label="Envio de dados pro Omie"
          value={envio.valor}
          context={envio.contexto}
        />
        <KpiCard
          label="Dias só em leitura"
          value={`${status.diasReadOnly} / 14`}
          context={
            status.diasReadOnly < 14
              ? `Faltam ${14 - status.diasReadOnly} dia(s) de observação antes de poder liberar o envio.`
              : 'Período de observação cumprido — o envio já pode ser liberado.'
          }
        />
        <KpiCard
          label="Todos os tipos já sincronizaram"
          value={status.peloMenos1SyncCadaTipo ? 'Sim' : 'Ainda não'}
          context={
            status.peloMenos1SyncCadaTipo
              ? 'Os 5 tipos já foram buscados pelo menos uma vez.'
              : 'Algum dos 5 tipos nunca foi buscado. Use os botões abaixo.'
          }
        />
      </div>

      {/* BUG-007 fix: opção global de criar clientes que só existem no Omie. */}
      <Card variant="flat" className="p-4">
        <Checkbox
          checked={criarFaltantes}
          onChange={(e) => setCriarFaltantes(e.target.checked)}
          data-testid="checkbox-criar-faltantes"
          label={
            <span className="font-medium">
              Cadastrar no IGM os clientes que só existem no Omie
            </span>
          }
          // 🔴 #587 (19/10/2026, decisão do Matheus): o texto descreve o que a opção FAZ hoje (`pullClientesFromOmie`) — o
          // anterior dizia que "o cadastro oficial é o do IGM", o contrário da regra de 13/10. Ajuste ao texto dele, conferido no
          // código: entra "transportadora" (a lista do Omie traz todos os cadastros). Comportamento e padrão da opção: iguais.
          hint="Vale só pra sincronização de Clientes. Com a opção marcada, todo cadastro ativo do Omie (cliente, fornecedor, transportadora ou funcionário) que ainda não existe aqui com o mesmo CNPJ é criado no sistema. Cadastro inativo no Omie não entra. Vem desmarcada para não trazer cadastros que nunca foram atendidos."
        />
      </Card>

      {lastSyncResult && (
        <Alert variant="success" title="Sincronização concluída">
          <span className="break-words">{lastSyncResult}</span>
        </Alert>
      )}

      {status.empresasComCredenciais.length === 0 ? (
        <Alert variant="info" title="Nenhuma empresa liberada pra falar com o Omie">
          Peça a quem cuida do servidor pra preencher <code>OMIE_APP_KEY_*</code> e{' '}
          <code>OMIE_APP_SECRET_*</code> no arquivo <code>.env</code>. Enquanto isso não
          acontecer, nenhuma sincronização roda.
        </Alert>
      ) : (
        <div className="space-y-4" data-testid="omie-empresas-lista">
          {status.empresasComCredenciais.map((empresaId) => {
            const credsR = credsResult[empresaId];
            return (
              <Card
                key={empresaId}
                variant="flat"
                className="p-4 sm:p-5 space-y-4"
                data-testid={`omie-empresa-card-${empresaId}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
                      Empresa
                    </div>
                    <div className="min-w-0 break-words font-mono text-sm text-ink-1 dark:text-ink-dark-1">
                      {empresaId}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    loading={testingEmpresa === empresaId}
                    onClick={() => testarCredenciais(empresaId)}
                    data-testid={`test-creds-btn-${empresaId}`}
                  >
                    Testar acesso
                  </Button>
                </div>

                {credsR && (
                  <div
                    className="flex flex-wrap items-center gap-2"
                    data-testid={`creds-result-${empresaId}`}
                  >
                    <Badge variant={credsR.ok ? 'success' : 'danger'} size="sm">
                      {CREDS_STATUS[credsR.status] ?? 'Resultado desconhecido'}
                    </Badge>
                    <span className="min-w-0 break-words text-xs text-ink-2 dark:text-ink-dark-2">
                      {credsR.message}
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {TIPOS.map((tipo) => {
                    const last = status.ultimosSync.find(
                      (u) => u.empresa_id === empresaId && u.tipo === tipo,
                    );
                    const key = `${empresaId}:${tipo}`;
                    return (
                      <div
                        key={tipo}
                        className="flex min-w-0 flex-col gap-2 rounded-xl border border-line dark:border-line-dark p-3"
                      >
                        <div className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                          {TIPO_LABEL[tipo]}
                        </div>
                        {(() => {
                          const e = status.execucoes?.find((x) => x.empresaId === empresaId && x.tipo === tipo);
                          const a = status.atrasos?.find((x) => x.empresaId === empresaId && x.tipo === tipo);
                          /* 🔴 19/10/2026: bloco ATRASADO deixa de mostrar "OK". Uma linha (regra do aviso, 09/10); o detalhe
                             — última vez, cadência, último erro — abre no lugar, ao clicar. */
                          if (a?.atrasado) {
                            return (
                              <Aviso
                                tom="atencao"
                                fato={`Atrasado · sem leitura ${tempoSemLeitura(a.minutosSemLeitura)}`}
                                data-testid={`atraso-${empresaId}-${tipo}`}
                              >
                                <div className="space-y-1 text-xs">
                                  <div>Última vez: {formatQuando(a.ultimaConcluidaIso)}</div>
                                  <div>
                                    Devia rodar a cada {a.cadenciaMinutos} minutos. Fica atrasado depois de {a.limiteMinutos} minutos
                                    sem terminar uma leitura.
                                  </div>
                                  {a.ultimoErro ? <div className="break-words text-danger">Último erro: {a.ultimoErro}</div> : null}
                                  {e ? <ExecucaoResumo e={e} /> : null}
                                </div>
                              </Aviso>
                            );
                          }
                          return (
                            <>
                              <div className="min-w-0 break-words text-xs text-ink-3 dark:text-ink-dark-3">
                                Última vez: {formatQuando(a?.ultimaConcluidaIso ?? last?.ultimo_finalizado)}
                              </div>
                              {e ? <ExecucaoResumo e={e} /> : null}
                            </>
                          );
                        })()}
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-auto w-full"
                          loading={syncingKey === key}
                          onClick={() => forcarSync(tipo, empresaId)}
                          data-testid={`sync-btn-${empresaId}-${tipo}`}
                        >
                          Sincronizar agora
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Alert variant="info" title="Como o envio de dados pro Omie é liberado">
        Não dá pra ligar por esta tela. Quem cuida do servidor precisa ajustar{' '}
        <code>OMIE_WRITE_ENABLED</code> e <code>OMIE_WRITE_MODE</code> no arquivo{' '}
        <code>.env</code>. De fábrica os dois vêm desligados — o IGM só lê o Omie.{' '}
        <HelpTip texto="É proposital: o desligamento é a proteção contra alguém gravar dado errado no financeiro por engano." />
      </Alert>
    </div>
  );
}
